import Phaser from 'phaser';
import { GROWTH_TICK } from '../data/world-config';
import { PLANTS } from '../data/plants';
import type { FarmPlot } from '../data/world-config';
import type { GameScene } from '../scenes/GameScene';

export class PlantGrowthSystem {
  private scene: GameScene;
  private lastTick: number = 0;
  private weedTimer: number = 0;
  private weedInterval: number = 25_000; // weed spawns every ~25s

  constructor(scene: GameScene) {
    this.scene = scene;
  }

  update(time: number, delta: number): void {
    if (time - this.lastTick < GROWTH_TICK) return;
    this.lastTick = time;

    // Process sprinklers first (auto-water)
    this.scene.farmSystem.processSprinklers();

    const plots = this.scene.farmSystem.getPlots();
    const plantSprites = this.scene.farmSystem.getPlantSprites();

    plots.forEach((plot, key) => {
      if (!plot.plantId) return;

      const species = PLANTS[plot.plantId];
      if (!species) return;

      // Growth requires water
      if (plot.waterLevel <= 0) {
        // Dry plant loses quality
        if (plot.quality !== undefined) {
          plot.quality = Math.max(0, plot.quality - 5);
        }
        return;
      }

      if (plot.growthStage >= 3) return;

      // Day/night growth modifier
      const isNight = this.scene.dayNightSystem.isNight();
      let growthModifier = 1.0;

      if (species.type === 'day' && !isNight) {
        growthModifier = 1.5;
      } else if (species.type === 'night' && isNight) {
        growthModifier = 1.5;
      } else if (species.type === 'day' && isNight) {
        growthModifier = 0.3;
      } else if (species.type === 'night' && !isNight) {
        growthModifier = 0.3;
      }

      // Fertilizer bonus: 2x growth
      if (plot.fertilized) {
        growthModifier *= 2.0;
      }

      // Adjacent weed penalty: 50% slower
      if (this.scene.farmSystem.hasAdjacentWeed(plot.x, plot.y)) {
        growthModifier *= 0.5;
      }

      // Overwater risk/reward: higher water = faster growth but wilt risk
      if (plot.waterLevel >= 5) {
        growthModifier *= 1.6;
        // 40% wilt chance per tick at max water
        if (Math.random() < 0.4) {
          this.wiltPlant(plot, key, plantSprites);
          return;
        }
      } else if (plot.waterLevel >= 4) {
        growthModifier *= 1.3;
        // 20% wilt chance per tick at water level 4
        if (Math.random() < 0.2) {
          this.wiltPlant(plot, key, plantSprites);
          return;
        }
      }

      // Consume water
      plot.waterLevel = Math.max(0, plot.waterLevel - 0.2);

      // Quality improves when well-watered
      if (plot.quality !== undefined && plot.waterLevel > 2) {
        plot.quality = Math.min(100, plot.quality + 2);
      }

      // Growth chance
      const growthChance = (GROWTH_TICK / (species.growthTime * 1000)) * growthModifier;
      if (Math.random() < growthChance) {
        this.growPlant(plot, key, plantSprites);
      }
    });

    // Weed spawning
    this.weedTimer += GROWTH_TICK;
    if (this.weedTimer >= this.weedInterval) {
      this.weedTimer = 0;
      if (Math.random() < 0.4) {
        this.scene.farmSystem.spawnWeed();
      }
    }
  }

  private growPlant(
    plot: FarmPlot,
    key: string,
    plantSprites: Map<string, Phaser.GameObjects.Sprite>
  ): void {
    if (!plot.plantId) return;

    plot.growthStage = Math.min(3, plot.growthStage + 1);

    const species = PLANTS[plot.plantId];
    if (!species) return;

    const sprite = plantSprites.get(key);
    if (sprite) {
      const newFrame = species.spriteRow * 4 + plot.growthStage;
      sprite.setFrame(newFrame);

      // Growth pop animation
      this.scene.tweens.add({
        targets: sprite,
        scaleX: 1.3,
        scaleY: 1.3,
        duration: 150,
        yoyo: true,
        ease: 'Back.easeOut',
      });

      this.createGrowthParticle(sprite.x, sprite.y);
    }

    if (plot.growthStage >= 3) {
      this.scene.events.emit('notification', `${species.name} is ready to harvest!`);
    }
  }

  private wiltPlant(
    plot: FarmPlot,
    key: string,
    plantSprites: Map<string, Phaser.GameObjects.Sprite>
  ): void {
    if (!plot.plantId) return;

    const species = PLANTS[plot.plantId];
    if (!species) return;

    // Wilt: drop to growth stage 1 max, halve quality
    plot.growthStage = Math.min(plot.growthStage, 1);
    if (plot.quality !== undefined) {
      plot.quality = Math.floor(plot.quality / 2);
    }
    // Drain excess water
    plot.waterLevel = Math.min(plot.waterLevel, 2);

    const sprite = plantSprites.get(key);
    if (sprite) {
      sprite.setFrame(species.spriteRow * 4 + plot.growthStage);

      // Wilt visual: yellow flash + shrink
      sprite.setTint(0xcccc44);
      this.scene.tweens.add({
        targets: sprite,
        scaleX: 0.7,
        scaleY: 0.7,
        duration: 300,
        yoyo: true,
        ease: 'Power2',
        onComplete: () => sprite.clearTint(),
      });
    }

    this.scene.events.emit('notification', `${species.name} wilted from overwatering!`);
  }

  private createGrowthParticle(x: number, y: number): void {
    for (let i = 0; i < 3; i++) {
      const particle = this.scene.add.circle(
        x + Phaser.Math.Between(-6, 6),
        y + Phaser.Math.Between(-6, 6),
        2,
        0x6aaa3a,
        0.8
      );
      particle.setDepth(15);

      this.scene.tweens.add({
        targets: particle,
        y: particle.y - 10,
        alpha: 0,
        duration: 600,
        delay: i * 100,
        onComplete: () => particle.destroy(),
      });
    }
  }
}
